/**
 * AuthProvider — kapselt die beiden Auth-Modi (Muster rmnc-personal §5).
 *
 *  - "dev":   Identitaet kommt aus dem Store (X-Dev-User-Header), kein Logto.
 *  - "logto": LogtoProvider (@logto/react) + Token-Provider-Registrierung
 *             fuer den Axios-Interceptor; nicht angemeldete Benutzer werden
 *             zum Logto-Login umgeleitet (F1: jeder Kunden-Logto-User darf).
 */

import { useEffect, useState, type ReactNode } from "react"
import { LogtoProvider, UserScope, useLogto, type LogtoConfig } from "@logto/react"
import { useLocation } from "react-router-dom"
import { LoginScreen } from "@/components/auth/LoginScreen"
import { fremdeLogtoSitzungenAngleichen } from "@/lib/sessionAbgleich"
import { setLogoutHandler, setTokenProvider, useAuthStore, AUTH_MODE } from "@/store/auth"

const LOGTO_ENDPOINT = import.meta.env.VITE_LOGTO_ENDPOINT ?? ""
const LOGTO_APP_ID = import.meta.env.VITE_LOGTO_APP_ID ?? ""
const LOGTO_RESOURCE = import.meta.env.VITE_LOGTO_RESOURCE ?? ""

// Redirect: <origin>/callback (U6: Portal-Subdomain, kein Präfix)
const callbackUrl = () => new URL(import.meta.env.BASE_URL + "callback", window.location.origin).href

const logtoConfig: LogtoConfig = {
  endpoint: LOGTO_ENDPOINT,
  appId: LOGTO_APP_ID,
  // roles-Scope: Rollen im ID-Token (Frontend blendet danach ein/aus;
  // durchgesetzt wird serverseitig ueber den Access-Token-Claim)
  scopes: [UserScope.Roles, UserScope.Email],
  ...(LOGTO_RESOURCE ? { resources: [LOGTO_RESOURCE] } : {}),
}

function LogtoBridge({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading, getAccessToken, getIdTokenClaims, signIn, signOut } =
    useLogto()
  const setUser = useAuthStore((s) => s.setUser)
  const [claimsLoaded, setClaimsLoaded] = useState(false)
  // Auf der Callback-Route KEIN signIn ausloesen: erst muss
  // useHandleSignInCallback (CallbackPage) den Code einloesen — sonst
  // Redirect-Schleife Login → callback → Login.
  const istCallback = useLocation().pathname === "/callback"

  // Token-Provider in einem EIGENEN Effect, unabhaengig von isLoading
  // (Muster rmnc-personal): das SDK flackert isLoading bei jedem
  // Token-Exchange — haengt der Provider daran, gehen Requests in diesem
  // Fenster ohne Bearer-Token raus (401-Sturm nach dem Login).
  useEffect(() => {
    setTokenProvider(async () => {
      const token = LOGTO_RESOURCE
        ? await getAccessToken(LOGTO_RESOURCE)
        : await getAccessToken()
      return token ?? null
    })
    return () => setTokenProvider(null)
  }, [getAccessToken])

  // Abmelden-Handler (TopBar): Logto-signOut mit Post-Logout-Redirect auf
  // die Tester-Startseite (URI muss in der Logto-App registriert sein)
  useEffect(() => {
    setLogoutHandler(() =>
      signOut(new URL(import.meta.env.BASE_URL, window.location.origin).href)
    )
    return () => setLogoutHandler(null)
  }, [signOut])

  // Nicht angemeldet → automatischer Login-Redirect; die Anmeldeseite
  // (Design Screen 01) bleibt waehrenddessen als Zwischenstand sichtbar
  useEffect(() => {
    if (istCallback || isLoading || isAuthenticated) return
    void signIn(callbackUrl())
  }, [istCallback, isLoading, isAuthenticated, signIn])

  // Angemeldet → User-Info (sub/name/roles) fuer die UI-Gates in den Store
  useEffect(() => {
    if (!isAuthenticated) {
      setUser(null)
      setClaimsLoaded(false)
      return
    }
    let cancelled = false
    void getIdTokenClaims().then((claims) => {
      if (cancelled || !claims) return
      const roles = Array.isArray(claims.roles) ? claims.roles.map(String) : []
      setUser({
        id: claims.sub,
        name: String(claims.name ?? claims.username ?? ""),
        email: claims.email ? String(claims.email) : null,
        roles,
      })
      // Veraltete Sitzungen der eingebetteten App (anderer Benutzer) räumen —
      // sie holt sich beim iframe-Laden die SSO-Identität des Tester-Logins
      fremdeLogtoSitzungenAngleichen(LOGTO_APP_ID, claims.sub)
      setClaimsLoaded(true)
    })
    return () => {
      cancelled = true
    }
  }, [isAuthenticated, getIdTokenClaims, setUser])

  // Callback-Route immer durchlassen (CallbackPage verarbeitet den Code)
  if (istCallback) return <>{children}</>

  // Gate auf Authentifizierung + geladene Claims — NICHT auf isLoading:
  // sonst unmountet die App bei jedem Token-Exchange (Remount-Schleife,
  // sichtbar als Flackern). Bis dahin dient die Anmeldeseite als
  // Zwischenstand (der Redirect laeuft bereits, Button deaktiviert).
  if (!isAuthenticated || !claimsLoaded) {
    return <LoginScreen onLogin={() => void signIn(callbackUrl())} laeuft />
  }
  return <>{children}</>
}

export function AuthProvider({ children }: { children: ReactNode }) {
  if (AUTH_MODE === "dev") {
    // Identitaet ist sofort im Store (auth.ts) — nichts zu tun
    return <>{children}</>
  }
  return (
    <LogtoProvider config={logtoConfig}>
      <LogtoBridge>{children}</LogtoBridge>
    </LogtoProvider>
  )
}
